import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { Asset } from "@/contracts";

import { deleteAssetMutation } from "@/api/queries";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useApiClient } from "@/lib/api-context";

/**
 * Soft-deletes an asset. The API refuses while live devices are attached
 * (ADR-090), so the dialog says so up front instead of offering a button
 * that can only fail.
 */
export function AssetDeleteDialog({ asset, onClose }: { asset: Asset; onClose: () => void }) {
  const { t } = useTranslation("pages");
  const client = useApiClient();
  const queryClient = useQueryClient();
  const blocked = asset.deviceCount > 0;

  const mutation = useMutation({
    ...deleteAssetMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["assets"] });
      // The site's asset count drops.
      await queryClient.invalidateQueries({ queryKey: ["sites"] });
      onClose();
    },
  });

  return (
    <ConfirmDialog
      open
      title={t("deleteAssetTitle")}
      confirmLabel={t("deleteAsset")}
      destructive
      pending={mutation.isPending}
      confirmDisabled={blocked}
      onConfirm={() => mutation.mutate({ id: asset.id })}
      onCancel={() => {
        if (!mutation.isPending) {
          onClose();
        }
      }}
    >
      {blocked ? (
        <p>{t("deleteAssetBlocked", { name: asset.name, count: asset.deviceCount })}</p>
      ) : (
        <p>{t("deleteAssetBody", { name: asset.name })}</p>
      )}
      {mutation.isError ? (
        <p className="mt-2 font-medium text-danger">{mutation.error.message}</p>
      ) : null}
    </ConfirmDialog>
  );
}
